import { ClientError } from '@criteria-labs/openapi-runtime'
import { SpinnerPane } from '@criteria-labs/react'
import { notFound } from 'next/navigation'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { Document } from '../documents/Document'
import { DocumentStore } from '../documents/DocumentStore'
import { OpenAPIDocument } from '../documents/OpenAPIDocument'
import { SpectralRulesetDocument } from '../documents/SpectralRulesetDocument'
import { friendlyID } from '../lib/friendlyID'
import { invariant } from '../lib/invariant'
import { ProjectManager } from '../project'
import { useRunSpectralLint } from '../workers/useRunSpectralLint'
import { downloadProject } from './downloadProject'
import { uploadProject } from './uploadProject'

export type PlaygroundSnapshot = {
  id: string
  view: 'editor' | 'docs'
}

export type PlaygroundInfo = {
  snapshot: PlaygroundSnapshot | null
  snapshotHref: string | null
  snapshotEdited: boolean
  createSnapshot: (view: 'editor' | 'docs') => Promise<void>
}

const PlaygroundContext = createContext<PlaygroundInfo | null>(null)

export function usePlaygroundInfo() {
  const context = useContext(PlaygroundContext)
  invariant(context, 'usePlaygroundInfo() must be called within a <Playground /> component')
  return context
}

export function Playground({
  defaultSnapshotID,
  defaultView,
  hrefForSnapshot,
  onCreateSnapshot,
  fallback,
  children
}: {
  defaultSnapshotID: string | null
  defaultView: 'editor' | 'docs'
  hrefForSnapshot: (snapshot: PlaygroundSnapshot) => string
  onCreateSnapshot?: (snapshot: PlaygroundSnapshot) => void
  fallback?: ReactNode | ((error: Error) => ReactNode)
  children?: ReactNode
}) {
  const [snapshotID, setSnapshotID] = useState<string | null>(defaultSnapshotID)
  const [snapshot, setSnapshot] = useState<PlaygroundSnapshot | null>(null)
  const needsLoad = snapshotID && !snapshot

  const [isLoading, setLoading] = useState(needsLoad ? true : false)
  const [error, setError] = useState<Error | null>(null)

  const [snapshotEdited, setSnapshotEdited] = useState(false)
  const hasUnsavedChanges = snapshot && snapshotEdited

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }
    const callback = (event: BeforeUnloadEvent) => {
      // Recommended
      event.preventDefault()

      // Included for legacy support, e.g. Chrome/Edge < 119
      event.returnValue = true
    }
    window.addEventListener('beforeunload', callback)
    return () => {
      window.removeEventListener('beforeunload', callback)
    }
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (!needsLoad) {
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const allFiles = await downloadProject(snapshotID)
        if (Object.keys(allFiles).length > 0 && !cancelled) {
          setSnapshot({ id: snapshotID, view: defaultView })
          await ProjectManager.shared().replaceAll(allFiles)
        }
      } catch (error) {
        setError(error as Error)
      } finally {
        setLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true // this helps with React strict mode
    }
  }, [needsLoad, snapshotID, defaultView])

  const handleCreateSnapshot = async (view: 'editor' | 'docs') => {
    const projectID = friendlyID('proj')
    await uploadProject(projectID)
    const createdSnapshot = { id: projectID, view }
    setSnapshotID(createdSnapshot.id)
    setSnapshot(createdSnapshot)
    setSnapshotEdited(false)
    if (onCreateSnapshot) {
      onCreateSnapshot(createdSnapshot)
    }
  }

  useEffect(() => {
    if (!snapshot || isLoading) {
      return
    }
    const handleProjectChange = () => {
      if (!snapshot || isLoading) {
        return
      }
      setSnapshotEdited(true)
    }
    const handleDocumentChange = (document: Document, change: 'text' | 'data') => {
      if (!snapshot || isLoading) {
        return
      }
      if (change === 'text') {
        setSnapshotEdited(true)
      }
    }
    ProjectManager.shared().addListener(handleProjectChange)
    DocumentStore.shared().addDocumentListener(handleDocumentChange)
    return () => {
      ProjectManager.shared().removeListener(handleProjectChange)
      DocumentStore.shared().removeDocumentListener(handleDocumentChange)
    }
  }, [snapshot, isLoading, setSnapshotEdited])

  const runSpectralLint = useRunSpectralLint()
  useEffect(() => {
    const handleDocumentChange = (document: Document, change: 'text' | 'data') => {
      if ((change === 'text' && document instanceof OpenAPIDocument) || document instanceof SpectralRulesetDocument) {
        runSpectralLint()
      }
    }
    DocumentStore.shared().addDocumentListener(handleDocumentChange)
    return () => {
      DocumentStore.shared().removeDocumentListener(handleDocumentChange)
    }
  }, [runSpectralLint])

  if (isLoading) {
    return <SpinnerPane />
  }

  if (error && error instanceof ClientError && error.problem?.status === 404) {
    return notFound()
  }

  if (error && !fallback) {
    throw error
  }

  const playgroundInfo = {
    snapshot,
    snapshotHref: snapshot ? hrefForSnapshot(snapshot) : null,
    snapshotEdited,
    createSnapshot: handleCreateSnapshot
  }

  return (
    <PlaygroundContext.Provider value={playgroundInfo}>
      {error && typeof fallback === 'function' && <>{fallback(error)}</>}
      {error && typeof fallback !== 'function' && <>{fallback}</>}
      {!error && children}
    </PlaygroundContext.Provider>
  )
}
